import React, { useState, useEffect } from "react";
import { X, FileText } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { postRequest } from "../../services";
import { useSelector, useDispatch } from "react-redux";
import { createAdminObservation } from "../../redux/adminObservationSlice";

const AdminObservationsModal = ({ isOpen, onClose, teacherEmail, campusClasses, onObservationAdded }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { register, handleSubmit, reset, watch, setValue, trigger } = useForm();
  const [currentSection, setCurrentSection] = useState(1);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  
  const { loading: isSubmitting, error } = useSelector((state) => state.adminObservation);
  
  const user = JSON.parse(localStorage.getItem("user"));
  const observerName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}` 
    : user?.email || "";

  const teachers = useSelector((state) => state.teachers.teachers || []);

  // Get unique classes from campusClasses
  const uniqueClasses = campusClasses?.flatMap((campus) =>
    campus.classes?.map((cls) => ({
      id: cls.id,
      name: cls.className,
      campusId: campus.id,
    }))
  ) || [];

  // Get all subjects with class reference from campusClasses
  const allSubjects = campusClasses?.flatMap((campus) =>
    campus.classes?.flatMap((cls) =>
      cls.sections?.flatMap((section) =>
        section.subjects?.map((subject) => ({
          id: subject.id,
          name: subject.subjectName,
          classId: cls.id,
        }))
      )
    )
  ) || [];

  // Get unique teachers
  const uniqueTeachers = teachers?.map((teacher) => ({
    email: teacher.teacher?.email,
    name: `${teacher.teacher?.firstName || ""} ${teacher.teacher?.lastName || ""}`.trim(),
  })).filter((t) => t.name) || [];

  const selectedClassId = watch("classGrade");
  const selectedCampusId = watch("campusId");

  // Filter classes based on selected campus
  const filteredClasses = selectedCampusId
    ? uniqueClasses.filter((cls) => cls.campusId === selectedCampusId)
    : uniqueClasses;

  // Filter subjects based on selected class
  const filteredSubjects = selectedClassId
    ? allSubjects.filter((subject) => subject.classId === selectedClassId)
    : allSubjects;

  useEffect(() => {
    if (isOpen) {
      setValue("observerName", observerName);
      setValue("teacherEmail", teacherEmail);
    }
  }, [isOpen, observerName, teacherEmail, setValue]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      setCurrentSection(1);
      setAttachments([]);
    }
  }, [isOpen, reset]);

  // Reset subject when class changes
  useEffect(() => {
    if (selectedClassId) {
      setValue("subject", "");
    }
  }, [selectedClassId, setValue]);

  const handleFileChange = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingFiles(true);
    const newAttachments = [...attachments];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileIndex = attachments.length + i;
      setUploadProgress((prev) => ({ ...prev, [fileIndex]: 0 }));
      
      try {
        const formData = new FormData();
        formData.append("files", file);
        
        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            const current = prev[fileIndex] || 0;
            if (current < 90) {
              return { ...prev, [fileIndex]: current + 10 };
            }
            return prev;
          });
        }, 200);
        
        const uploadResponse = await postRequest(
          "/api/Document/documents-upload",
          formData,
          true
        );
        
        clearInterval(progressInterval);
        setUploadProgress((prev) => ({ ...prev, [fileIndex]: 100 }));
        
        if (uploadResponse?.data?.[0]) {
          newAttachments.push({
            file: file,
            url: uploadResponse.data[0],
            name: file.name,
          });
        }
        
        // Clear progress after a short delay
        setTimeout(() => {
          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[fileIndex];
            return newProgress;
          });
        }, 500);
      } catch (error) {
        console.error("Error uploading file:", error);
        toast.error(`Failed to upload ${file.name}`);
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[fileIndex];
          return newProgress;
        });
      }
    }
    
    setAttachments(newAttachments);
    setUploadingFiles(false);
    e.target.value = ""; // Reset file input
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[index];
      return newProgress;
    });
  };

  const validateCurrentSection = async () => {
    let fieldsToValidate = [];
    
    switch (currentSection) {
      case 1:
        fieldsToValidate = [
          "observationDate",
          "observerName",
          "classGrade",
          "subject",
          "teacherName",
          "lessonTopic",
          "startDurationObservation",
          "endDurationObservation",
          "observationType",
        ];
        break;
      case 2:
        fieldsToValidate = [
          "classroomOrganization",
          "studentEngagement",
          "useOfLearningMaterials",
          "inclusivity",
          "teacherStudentInteraction",
        ];
        break;
      case 3:
        fieldsToValidate = [
          "lessonPlanning",
          "lessonPlanningDetails",
          "teachingStrategies",
          "questioningTechniques",
          "paceTimeManagement",
          "contentKnowledge",
          "technologyIntegration",
          "technologyIntegrationDetails",
        ];
        break;
      case 4:
        fieldsToValidate = [
          "assessmentMethods",
          "assessmentMethodsDetails",
          "studentParticipation",
          "studentParticipationDetails",
          "feedbackProvided",
          "feedbackProvidedDetails",
          "evidenceOfLearning",
          "evidenceOfLearningDetails",
        ];
        break;
      case 5:
        fieldsToValidate = [
          "preparedness",
          "preparednessDetails",
          "professionalDemeanor",
          "professionalDemeanorDetails",
          "collaboration",
          "collaborationDetails",
          "responsivenessToFeedback",
          "responsivenessToFeedbackDetails",
          "reflectivePractice",
          "reflectivePracticeDetails",
        ];
        break;
      case 6:
        fieldsToValidate = [
          "strengthsObserved",
          "areasForImprovement",
          "actionPlan",
          "overallRating",
          "overallRatingDetails",
        ];
        break;
      default:
        return true;
    }
    
    const isValid = await trigger(fieldsToValidate);
    if (!isValid) {
      toast.error(t("Please fill all required fields") || "Please fill all required fields in this section");
    }
    return isValid;
  };

  const nextSection = async () => {
    const isValid = await validateCurrentSection();
    if (isValid && currentSection < 7) {
      const nextSectionNum = currentSection + 1;
      
      // Clear fields for the next section to prevent pre-filled values
      // This ensures fields start fresh when navigating to next step
      const fieldsToClear = {
        3: ["teachingStrategies", "contentKnowledge", "questioningTechniques", "paceTimeManagement"],
        4: ["assessmentMethods", "assessmentMethodsDetails"],
        6: ["overallRating", "overallRatingDetails"],
      };
      
      if (fieldsToClear[nextSectionNum]) {
        fieldsToClear[nextSectionNum].forEach((fieldName) => {
          setValue(fieldName, "", { shouldValidate: false, shouldDirty: false });
        });
      }
      
      setCurrentSection(nextSectionNum);
    }
  };

  const prevSection = () => {
    if (currentSection > 1) {
      setCurrentSection(currentSection - 1);
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error || t("Failed To Add Observation") || "Failed to add observation");
    }
  }, [error, t]);

  const onSubmit = async (data) => {
    // Validate last section before submitting
    const isValid = await validateCurrentSection();
    if (!isValid) {
      return;
    }

    try {
      // Prepare attachments - use already uploaded URLs or upload remaining files
      const attachmentUrls = [];
      if (attachments.length > 0) {
        for (const attachment of attachments) {
          if (attachment.url) {
            // Already uploaded
            attachmentUrls.push(attachment.url);
          } else if (attachment.file) {
            // Need to upload
            setUploadingFiles(true);
            try {
              const formData = new FormData();
              formData.append("files", attachment.file);
              const uploadResponse = await postRequest(
                "/api/Document/documents-upload",
                formData,
                true
              );
              if (uploadResponse?.data?.[0]) {
                attachmentUrls.push(uploadResponse.data[0]);
              }
            } catch (error) {
              console.error("Error uploading file:", error);
              toast.error(`Failed to upload ${attachment.name || attachment.file.name}`);
            } finally {
              setUploadingFiles(false);
            }
          }
        }
      }

      const payload = {
        teacherEmail: data.teacherEmail || teacherEmail,
        basicInformation: {
          observationDate: data.observationDate,
          observerName: data.observerName,
          observerDesignation: data.observerDesignation || user?.designation || "",
          classGrade: data.classGrade,
          subject: data.subject,
          teacherName: data.teacherName,
          lessonTopic: data.lessonTopic,
          startDurationObservation: data.startDurationObservation,
          endDurationObservation: data.endDurationObservation,
          observationType: data.observationType,
        },
        classroomEnvironment: {
          classroomOrganization: data.classroomOrganization,
          studentEngagement: data.studentEngagement,
          useOfLearningMaterials: data.useOfLearningMaterials,
          useOfLearningMaterialsDetails: data.useOfLearningMaterialsDetails,
          inclusivity: data.inclusivity,
          inclusivityDetails: data.inclusivityDetails,
          teacherStudentInteraction: data.teacherStudentInteraction,
        },
        instructionalDelivery: {
          lessonPlanning: data.lessonPlanning,
          lessonPlanningDetails: data.lessonPlanningDetails,
          teachingStrategies: data.teachingStrategies,
          questioningTechniques: data.questioningTechniques,
          paceTimeManagement: data.paceTimeManagement,
          contentKnowledge: data.contentKnowledge,
          technologyIntegration: data.technologyIntegration,
          technologyIntegrationDetails: data.technologyIntegrationDetails,
        },
        studentLearningAssessment: {
          assessmentMethods: data.assessmentMethods,
          assessmentMethodsDetails: data.assessmentMethodsDetails,
          studentParticipation: data.studentParticipation,
          studentParticipationDetails: data.studentParticipationDetails,
          feedbackProvided: data.feedbackProvided,
          feedbackProvidedDetails: data.feedbackProvidedDetails,
          evidenceOfLearning: data.evidenceOfLearning,
          evidenceOfLearningDetails: data.evidenceOfLearningDetails,
        },
        professionalismReflection: {
          preparedness: data.preparedness,
          preparednessDetails: data.preparednessDetails,
          professionalDemeanor: data.professionalDemeanor,
          professionalDemeanorDetails: data.professionalDemeanorDetails,
          collaboration: data.collaboration,
          collaborationDetails: data.collaborationDetails,
          responsivenessToFeedback: data.responsivenessToFeedback,
          responsivenessToFeedbackDetails: data.responsivenessToFeedbackDetails,
          reflectivePractice: data.reflectivePractice,
          reflectivePracticeDetails: data.reflectivePracticeDetails,
        },
        principalsRemarks: {
          strengthsObserved: data.strengthsObserved,
          areasForImprovement: data.areasForImprovement,
          actionPlan: data.actionPlan,
          followUpDate: data.followUpDate || null,
          overallRating: data.overallRating,
          overallRatingDetails: data.overallRatingDetails,
        },
        attachments: attachmentUrls,
      };

      const result = await dispatch(createAdminObservation(payload)).unwrap();
      if (result) {
        toast.success(t("Observation Added Successfully") || "Observation added successfully");
        reset();
        setCurrentSection(1);
        setAttachments([]);
        setUploadProgress({});
        // Refresh observations list if callback provided
        if (onObservationAdded) {
          onObservationAdded();
        }
        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error("Error submitting observation:", error);
      // Error toast is handled by useEffect watching the error state
    }
  };

  if (!isOpen) return null;

  const renderSection = () => {
    switch (currentSection) {
      case 1:
        return (
          <div key="section-1" className="space-y-4">
            <h3 className="text-xl font-bold text-blue-500 mb-4">
              {t("Basic Information") || "1. Basic Information"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 font-medium">
                  {t("Observation Date") || "Observation Date"} *
                </label>
                <input
                  type="date"
                  {...register("observationDate", { required: true })}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Observer Name") || "Observer Name / Designation"} *
                </label>
                <input
                  type="text"
                  {...register("observerName", { required: true })}
                  className="w-full p-2 border rounded"
                  defaultValue={observerName}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Observer Designation") || "Observer Designation"}
                </label>
                <input
                  type="text"
                  {...register("observerDesignation")}
                  className="w-full p-2 border rounded"
                  defaultValue={user?.designation || ""}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Campus") || "Campus"}
                </label>
                <select
                  {...register("campusId")}
                  className="w-full p-2 border rounded"
                >
                  <option value="">{t("Select Campus") || "Select Campus"}</option>
                  {campusClasses?.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Class Grade") || "Class / Grade"} *
                </label>
                <select
                  {...register("classGrade", { required: true })}
                  className="w-full p-2 border rounded"
                >
                  <option value="">{t("Select Class") || "Select Class"}</option>
                  {filteredClasses.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Subject") || "Subject"} *
                </label>
                <select
                  {...register("subject", { required: true })}
                  className="w-full p-2 border rounded"
                  disabled={!selectedClassId}
                >
                  <option value="">{selectedClassId ? (t("Select Subject") || "Select Subject") : (t("Select Class First") || "Select Class First")}</option>
                  {filteredSubjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Teacher Name") || "Teacher Name"} *
                </label>
                <select
                  {...register("teacherName", { required: true })}
                  className="w-full p-2 border rounded"
                >
                  <option value="">{t("Select Teacher") || "Select Teacher"}</option>
                  {uniqueTeachers.map((teacher, idx) => (
                    <option key={idx} value={teacher.email}>
                      {teacher.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Lesson Topic") || "Lesson Topic"} *
                </label>
                <input
                  type="text"
                  {...register("lessonTopic", { required: true })}
                  className="w-full p-2 border rounded"
                  placeholder={t("Enter Lesson Topic") || "Enter lesson topic"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Start Duration Observation") || "Start Duration Observation"} *
                </label>
                <input
                  type="datetime-local"
                  {...register("startDurationObservation", { required: true })}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("End Duration Observation") || "End Duration Observation"} *
                </label>
                <input
                  type="datetime-local"
                  {...register("endDurationObservation", { required: true })}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Type of Observation") || "Type of Observation"} *
                </label>
                <select
                  {...register("observationType", { required: true })}
                  className="w-full p-2 border rounded"
                >
                  <option value="">{t("Select Type") || "Select Type"}</option>
                  <option value="Scheduled">{t("Scheduled") || "Scheduled"}</option>
                  <option value="Unscheduled">{t("Unscheduled") || "Unscheduled"}</option>
                  <option value="Follow-up">{t("Follow Up") || "Follow-up"}</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div key="section-2" className="space-y-4">
            <h3 className="text-xl font-bold text-blue-500 mb-4">
              {t("Classroom Environment") || "2. Classroom Environment"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium">
                  {t("Classroom Organization") || "Classroom Organization"} *
                </label>
                <textarea
                  {...register("classroomOrganization", { required: true })}
                  rows="3"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Classroom Organization") || "Describe classroom organization, neatness, resources arrangement"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Student Engagement Behavior") || "Student Engagement / Behavior"} *
                </label>
                <textarea
                  {...register("studentEngagement", { required: true })}
                  rows="3"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Student Engagement") || "Describe student engagement and behavior"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Use of Learning Materials") || "Use of Learning Materials"} *
                </label>
                <select
                  {...register("useOfLearningMaterials", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("useOfLearningMaterialsDetails")}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Provide Details") || "Provide details"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Inclusivity Differentiation") || "Inclusivity / Differentiation"} *
                </label>
                <select
                  {...register("inclusivity", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("inclusivityDetails")}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Attention To Diverse Learners") || "Describe attention to diverse learners"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Teacher Student Interaction Quality") || "Teacher–Student Interaction Quality"} *
                </label>
                <textarea
                  {...register("teacherStudentInteraction", { required: true })}
                  rows="3"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Interaction Quality") || "Describe teacher-student interaction quality"}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div key="section-3" className="space-y-4">
            <h3 className="text-xl font-bold text-blue-500 mb-4">
              {t("Instructional Delivery") || "3. Instructional Delivery"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium">
                  {t("Lesson Planning Objectives") || "Lesson Planning & Objectives"} *
                </label>
                <select
                  {...register("lessonPlanning", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("lessonPlanningDetails", { required: true })}
                  rows="3"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Clarity And Alignment") || "Describe clarity and alignment with curriculum"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Teaching Strategies Methodology") || "Teaching Strategies / Methodology"} *
                </label>
                <textarea
                  {...register("teachingStrategies", { required: true })}
                  rows="3"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Teaching Strategies") || "Describe teaching strategies and methodology"}
                  autoComplete="new-password"
                  data-lpignore="true"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Questioning Techniques") || "Questioning Techniques"} *
                </label>
                <textarea
                  {...register("questioningTechniques", { required: true })}
                  rows="3"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Questioning Techniques") || "Describe questioning techniques"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Pace Time Management") || "Pace & Time Management"} *
                </label>
                <textarea
                  {...register("paceTimeManagement", { required: true })}
                  rows="3"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Pace And Time Management") || "Describe pace and time management"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Content Knowledge") || "Content Knowledge"} *
                </label>
                <textarea
                  {...register("contentKnowledge", { required: true })}
                  rows="3"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Content Knowledge") || "Describe content knowledge"}
                  autoComplete="new-password"
                  data-lpignore="true"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Integration Of Technology Aids") || "Integration of Technology / Aids"} *
                </label>
                <select
                  {...register("technologyIntegration", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("technologyIntegrationDetails", { required: true })}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Technology Integration") || "Describe technology integration"}
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div key="section-4" className="space-y-4">
            <h3 className="text-xl font-bold text-blue-500 mb-4">
              {t("Student Learning Assessment") || "4. Student Learning & Assessment"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium">
                  {t("Assessment Methods Used") || "Assessment Methods Used"} *
                </label>
                <select
                  {...register("assessmentMethods", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                  autoComplete="new-password"
                  data-lpignore="true"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Formative">{t("Formative") || "Formative"}</option>
                  <option value="Summative">{t("Summative") || "Summative"}</option>
                  <option value="Both">{t("Both") || "Both"}</option>
                </select>
                <textarea
                  {...register("assessmentMethodsDetails", { required: true })}
                  rows="3"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Assessment Methods") || "Describe assessment methods"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Student Participation") || "Student Participation"} *
                </label>
                <select
                  {...register("studentParticipation", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("studentParticipationDetails", { required: true })}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Student Participation") || "Describe student participation"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Feedback Provided To Students") || "Feedback Provided to Students"} *
                </label>
                <select
                  {...register("feedbackProvided", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("feedbackProvidedDetails", { required: true })}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Feedback Provided") || "Describe feedback provided"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Evidence Of Learning Outcomes") || "Evidence of Learning Outcomes"} *
                </label>
                <select
                  {...register("evidenceOfLearning", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("evidenceOfLearningDetails", { required: true })}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Evidence Of Learning") || "Describe evidence of learning outcomes"}
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div key="section-5" className="space-y-4">
            <h3 className="text-xl font-bold text-blue-500 mb-4">
              {t("Professionalism Reflection") || "5. Professionalism & Reflection"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium">
                  {t("Preparedness Punctuality") || "Preparedness & Punctuality"} *
                </label>
                <select
                  {...register("preparedness", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("preparednessDetails", { required: true })}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Preparedness") || "Describe preparedness and punctuality"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Professional Demeanor") || "Professional Demeanor"} *
                </label>
                <select
                  {...register("professionalDemeanor", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("professionalDemeanorDetails", { required: true })}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Professional Demeanor") || "Describe professional demeanor"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Collaboration With Colleagues") || "Collaboration with Colleagues"} *
                </label>
                <select
                  {...register("collaboration", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("collaborationDetails", { required: true })}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Collaboration") || "Describe collaboration with colleagues"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Responsiveness To Feedback") || "Responsiveness to Feedback"} *
                </label>
                <select
                  {...register("responsivenessToFeedback", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("responsivenessToFeedbackDetails", { required: true })}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Responsiveness") || "Describe responsiveness to feedback"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Reflective Practice") || "Reflective Practice (self-evaluation)"} *
                </label>
                <select
                  {...register("reflectivePractice", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                >
                  <option value="">{t("Select") || "Select"}</option>
                  <option value="Yes">{t("Yes") || "Yes"}</option>
                  <option value="No">{t("No") || "No"}</option>
                </select>
                <textarea
                  {...register("reflectivePracticeDetails", { required: true })}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Reflective Practice") || "Describe reflective practice"}
                />
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div key="section-6" className="space-y-4">
            <h3 className="text-xl font-bold text-blue-500 mb-4">
              {t("Principals Remarks") || "6. Principal's Remarks"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium">
                  {t("Strengths Observed") || "Strengths Observed"} *
                </label>
                <textarea
                  {...register("strengthsObserved", { required: true })}
                  rows="4"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Strengths") || "Describe strengths observed"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Areas For Improvement") || "Areas for Improvement"} *
                </label>
                <textarea
                  {...register("areasForImprovement", { required: true })}
                  rows="4"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Areas For Improvement") || "Describe areas for improvement"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Action Plan Recommendations") || "Action Plan / Recommendations"} *
                </label>
                <textarea
                  {...register("actionPlan", { required: true })}
                  rows="4"
                  className="w-full p-2 border rounded"
                  placeholder={t("Describe Action Plan") || "Describe action plan and recommendations"}
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Follow Up Date") || "Follow-up Date (if any)"}
                </label>
                <input
                  type="date"
                  {...register("followUpDate")}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">
                  {t("Overall Rating Score") || "Overall Rating / Score"} *
                </label>
                <select
                  {...register("overallRating", { required: true })}
                  className="w-full p-2 border rounded mb-2"
                  autoComplete="new-password"
                  data-lpignore="true"
                >
                  <option value="">{t("Select Rating") || "Select Rating"}</option>
                  <option value="Outstanding">{t("Outstanding") || "Outstanding"}</option>
                  <option value="Excellent">{t("Excellent") || "Excellent"}</option>
                  <option value="Good">{t("Good") || "Good"}</option>
                  <option value="Satisfactory">{t("Satisfactory") || "Satisfactory"}</option>
                  <option value="Needs Improvement">{t("Needs Improvement") || "Needs Improvement"}</option>
                </select>
                <textarea
                  {...register("overallRatingDetails", { required: true })}
                  rows="2"
                  className="w-full p-2 border rounded"
                  placeholder={t("Provide Rating Details") || "Provide rating details"}
                />
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div key="section-7" className="space-y-4">
            <h3 className="text-xl font-bold text-blue-500 mb-4">
              {t("Attachments Evidence") || "7. Attachments / Evidence"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium">
                  {t("Upload Photos Lesson Plans") || "Upload Photos / Lesson Plans / Student Work Samples"}
                </label>
                <div className="relative">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFileChange(e);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    accept="image/*,.pdf,.doc,.docx"
                    className="w-full p-2 border rounded"
                    disabled={uploadingFiles}
                    formNoValidate
                  />
                  {uploadingFiles && (
                    <div className="absolute inset-0 bg-blue-50 bg-opacity-75 flex items-center justify-center rounded">
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                        <span className="text-xs text-blue-600 font-medium">
                          {t("Uploading Files") || "Uploading files..."}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {t("You Can Upload Multiple Files") || "You can upload multiple files"}
                </p>
              </div>
              {attachments.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">
                    {t("Uploaded Files") || "Uploaded Files"}:
                  </h4>
                  <ul className="space-y-2">
                    {attachments.map((attachment, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex-1 flex items-center gap-3">
                          <FileText size={16} className="text-blue-500" />
                          <span className="text-sm font-medium text-gray-700">
                            {attachment.name || attachment.file?.name || `File ${index + 1}`}
                          </span>
                          {attachment.url && (
                            <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                              {t("Uploaded") || "Uploaded"}
                            </span>
                          )}
                          {uploadProgress[index] !== undefined && (
                            <div className="flex-1 max-w-xs">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${uploadProgress[index]}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600">{uploadProgress[index]}%</span>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded transition-colors"
                          disabled={uploadingFiles}
                        >
                          <X size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-2xl font-bold text-blue-500">
            {t("Admin Observations") || "Admin Observations"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto" autoComplete="off" noValidate>
          <div className="p-6">
            {/* Progress indicator */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">
                  {t("Step") || "Step"} {currentSection} {t("of") || "of"} 7
                </span>
                <span className="text-sm text-gray-500">
                  {Math.round((currentSection / 7) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentSection / 7) * 100}%` }}
                />
              </div>
            </div>

            {/* Section content */}
            {renderSection()}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between p-4 border-t bg-gray-50">
            <button
              type="button"
              onClick={prevSection}
              disabled={currentSection === 1}
              className={`px-4 py-2 rounded ${
                currentSection === 1
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
            >
              {t("Previous") || "Previous"}
            </button>
            <div className="flex gap-2">
              {currentSection < 7 ? (
                <button
                  type="button"
                  onClick={nextSection}
                  disabled={uploadingFiles}
                  className={`px-6 py-2 rounded font-medium transition-colors ${
                    uploadingFiles
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                      : "bg-blue-500 text-white hover:bg-blue-600"
                  }`}
                >
                  {t("Next") || "Next"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit(onSubmit)(e);
                  }}
                  disabled={isSubmitting || uploadingFiles}
                  className={`px-6 py-2 rounded font-medium transition-colors ${
                    isSubmitting || uploadingFiles
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed flex items-center gap-2"
                      : "bg-green-500 text-white hover:bg-green-600"
                  }`}
                >
                  {isSubmitting || uploadingFiles ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {isSubmitting ? (t("Submitting") || "Submitting...") : (t("Uploading Files") || "Uploading files...")}
                    </>
                  ) : (
                    t("Submit") || "Submit"
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminObservationsModal;

